import { FC, useEffect, useMemo, useState } from 'react'
import { Accordion } from "../../../../../../components/controls/accordion"
import styles from './styles/SettingsAdditionalModalContent.module.scss'
import { v4 as uuidv4 } from 'uuid';
import { DefaultGrid } from "../../../../../../components/grids/default/defaultGrid";
import useGridFilter from "../../../../../../system/hooks/useGridFilter";
import SettingsAdditionalPluginSettings from "./SettingsAdditionalPluginSettings";
import { Checkbox } from '../../../../../../components/checkboxes/checkbox'
import { useTranslation } from 'react-i18next';
import { IAdditionalSettingsViewDTO, ICashRegisterCreate, offlineVersion, versionTSPIoT } from '../../../../../../libs/coreapi-dto/service/cashRegisters';
import FlexColumn from '../../../../../../components/controls/FlexColumn';
import { IOption, Select } from '../../../../../../components/selects/select';
import { NumberInput, TextAreaInput, TextInput } from '../../../../../../components/controls/inputs';
import GridWrapper from '../../../../../../components/controls/GridWrapper';
import { useUserContext } from '../../../../../../system/providers/userContextProvider';
import FlexRow from '../../../../../../components/controls/FlexRow';

interface ISettingsAdditionalModalContentProps {
    lockFromPermission?: boolean,
    cashRegisterDto: ICashRegisterCreate
    setRegisterDto: (dto: ICashRegisterCreate) => void;
    onValidationChange?: (isValid: boolean, errors: string[]) => void;
}

export const SettingsAdditionalModalContent: FC<ISettingsAdditionalModalContentProps> = (props) => {
    const [gridFilter, dispatchGridFilter] = useGridFilter();
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedItem, setSelectedItem] = useState<IGridRow>()
    const [selectAdditionalSetting, setSelectAdditionalSetting] = useState<IAdditionalSettingsViewDTO>()
    const userContext = useUserContext()
    const isAdministrator = userContext.userRoles.includes('Administrator');

    const { t } = useTranslation();
    const errorsT = (value: string) => t('errors.' + value);

    const options = [
        { displayName: 'Не печатать', value: "Не печатать" },
        { displayName: 'Процент', value: "Процент" },
        { displayName: 'Сумма', value: "Сумма" },
    ] as IOption[]

    const offlineVersionsOptions = props.cashRegisterDto?.versionTSPIoT === "VER_3" ? [
        { displayName: 'VER_1.2', value: offlineVersion.VER_12 },
        { displayName: 'VER_2.1', value: offlineVersion.VER_21 },
    ] as IOption[] : [
        { displayName: 'VER_1.1', value: offlineVersion.VER_11 },
        { displayName: 'VER_1.2', value: offlineVersion.VER_12 },
        { displayName: 'VER_2.1', value: offlineVersion.VER_21 },
    ] as IOption[] 

    const versionTSPIoTOptions = [
        { displayName: 'VER_1', value: versionTSPIoT.VER_1 },
        { displayName: 'VER_2', value: versionTSPIoT.VER_2 },
        { displayName: 'VER_3', value: versionTSPIoT.VER_3 },
    ] as IOption[]

    const defaultTSPIoTSettings = {
        urlTSPIoT: 'https://esm-emu.ao-esp.ru',
        timeOut: 3,
        idTSPIoTWin: "9ff2fb82-e1f3-4e43-963e-2d1371ea25d0",
        idTSPIoTLin: "9ff2fb82-e1f3-4e43-963e-2d1371ea25d0",
        versionTSPIoT: versionTSPIoTOptions[versionTSPIoTOptions.length - 1],
        offlineLogin: 'admin',
        offlinePassword: 'admin',
        offlineServer: 'localhost',
        offlinePort: '5995',
        offlineToken: '',
        offlineVersion: offlineVersionsOptions[offlineVersionsOptions.length - 1], //Последняя(автовыбор из енама)
        offlineRegimeEnabled: false,
        allowChequePrintLMCZNoAnswer: false
    }

    const [additionalSettings, setAdditionalSettings] = useState<IAdditionalSettingsViewDTO[]>([]);

    const [urlTSPIoTError, setUrlTSPIoTError] = useState<string>('');
    const [messageOGVError, setMessageOGVError] = useState<string>('');

    const initialValueEndText = props.cashRegisterDto.endText
        ? props.cashRegisterDto.endText.replace(/\\n/g, '\n')
        : '';

    const handleEndTextChequeChange = (value: string) => {

        const normalizedText = value
            .replace(/\r\n/g, '\n')  // Windows (CR+LF) → Unix (LF)
            .replace(/\r/g, '\n')    // Old Mac (CR) → Unix (LF)
            .trim();

        props.setRegisterDto({
            ...props.cashRegisterDto,
            endText: normalizedText
        })
    };

    const validateUrlTSPIoT = (value: string): boolean => {
        if (props.cashRegisterDto.useTSPIoT && !value) {
            setUrlTSPIoTError(errorsT("required"));
            return false;
        }
        setUrlTSPIoTError('');
        return true;
    };
    const validateMessageOGV = (value: string): boolean => {
        if (props.cashRegisterDto.allowGoodsOGV && !value) {
            setMessageOGVError(errorsT("required"));
            return false;
        }
        setMessageOGVError('');
        return true;
    };

    const handleUrlTSPIoTChange = (value: string) => {
        validateUrlTSPIoT(value);
        props.setRegisterDto({
            ...props.cashRegisterDto,
            urlTSPIoT: value
        })
    };

    const handleMessageOGVChange = (value: string) => {
        validateMessageOGV(value);
        props.setRegisterDto({
            ...props.cashRegisterDto,
            messageOGV: value
        })
    };

    const updateValidation = () => {

        const isUrlTSPIoTValid = validateUrlTSPIoT(props.cashRegisterDto.urlTSPIoT || '');
        const isMessageOGVValid = validateMessageOGV(props.cashRegisterDto.messageOGV || '');

        const errors: string[] = [];

        if (urlTSPIoTError) errors.push(urlTSPIoTError);
        if (messageOGVError) errors.push(messageOGVError);

        const isValid = isUrlTSPIoTValid && isMessageOGVValid;

        if (props.onValidationChange) {
            props.onValidationChange(isValid, errors);
        }
    };

    useEffect(() => {
        if (props.cashRegisterDto.useTSPIoT) {
            validateUrlTSPIoT(props.cashRegisterDto.urlTSPIoT || '');
        } else {
            setUrlTSPIoTError('');
        }
        updateValidation();
    }, [props.cashRegisterDto.useTSPIoT]);

    useEffect(() => {
        validateUrlTSPIoT(props.cashRegisterDto.urlTSPIoT || '');
        updateValidation();
    }, [props.cashRegisterDto.urlTSPIoT]);

    useEffect(() => {
        if (props.cashRegisterDto.allowGoodsOGV) {
            validateMessageOGV(props.cashRegisterDto.messageOGV || '');
        } else {
            setMessageOGVError('');
        }
        updateValidation();
    }, [props.cashRegisterDto.allowGoodsOGV]);

    useEffect(() => {
        updateValidation();
    }, [urlTSPIoTError, messageOGVError]);

    useEffect(() => {
        validateUrlTSPIoT(props.cashRegisterDto.urlTSPIoT || '');
        updateValidation();
    }, [props.cashRegisterDto.urlTSPIoT]);

    useEffect(() => {
        validateMessageOGV(props.cashRegisterDto.messageOGV || '');
        updateValidation();
    }, [props.cashRegisterDto.messageOGV]);

    useEffect(() => {
        const additionalSettingsModel =
            [{
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.settingsTSPIoT"),
                serialNumber: 1,
                value: props.cashRegisterDto.useTSPIoT ?? false
            },
            {
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.enableMixPayment"),
                serialNumber: 2,
                value: props.cashRegisterDto.enableMixPayment ?? false
            },
            {
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.enableAcquiring"),
                serialNumber: 3,
                value: props.cashRegisterDto.enableAcquiring ?? false
            },
            {
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.enableProvisorInterface"),
                serialNumber: 4,
                value: props.cashRegisterDto.enableProvisorInterface ?? false
            },
            {
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.askBeforeChequePrint"),
                serialNumber: 5,
                value: props.cashRegisterDto.askBeforeChequePrint ?? false
            },
            {
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.visualProcessingRV"),
                serialNumber: 6,
                value: props.cashRegisterDto.visualProcessingRV ?? true
            },
            {
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.printDiscountSettings"),
                serialNumber: 7,
                data: props.cashRegisterDto.printDiscountSettings ?? {
                    printDiscountForItem: false,
                    printPriceForItemWithoutDiscount: false,
                    printSummDiscount: false,
                    printSummForChequeWithoutDiscount: false,
                    printSummForItemWithoutDiscount: false,
                    printTypeOfDiscount: options[0].value
                }
            },
            {
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.printSectionReport"),
                serialNumber: 8,
                value: props.cashRegisterDto.printSectionReport ?? false
            },
            {
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.endTextCheque"),
                serialNumber: 9,
                value: props.cashRegisterDto.endText ? true : false
            },
            {
                idGlobal: uuidv4(),
                setting: t("modals.additionalTab.isAutoUpdate"),
                serialNumber: 10,
                value: props.cashRegisterDto.isAutoUpdate ? true : false
            },
            ] as IAdditionalSettingsViewDTO[]

        setAdditionalSettings(additionalSettingsModel)
    }, [props.cashRegisterDto])

    useEffect(() => {
        if (selectedItem) setSelectAdditionalSetting(additionalSettings.find(x => x.idGlobal === selectedItem?.idGlobal))
        else setSelectAdditionalSetting(undefined)
    }, [selectedItem])

    const descriptionText = useMemo(() => {
        if (!selectedItem) return '';

        const setting = additionalSettings.find(x => x.idGlobal === selectedItem.idGlobal);
        if (!setting) return '';

        if (setting.serialNumber === 1) {
            return t("modals.additionalTab.settingsTSPIoTDescription");
        }
        return setting.setting;
    }, [selectedItem])

    useEffect(() => {
        if (props.cashRegisterDto.useTSPIoT && props.cashRegisterDto.versionTSPIoT !== "VER_1") {
            const updates: Partial<ICashRegisterCreate> = {};
            
            if (!props.cashRegisterDto.urlTSPIoT) {
                updates.urlTSPIoT = defaultTSPIoTSettings.urlTSPIoT
            }
            if (!props.cashRegisterDto.timeOut) {
                updates.timeOut = defaultTSPIoTSettings.timeOut
            }
            if (!props.cashRegisterDto.idTSPIoTWin) {
                updates.idTSPIoTWin = defaultTSPIoTSettings.idTSPIoTWin
            }
            if (!props.cashRegisterDto.idTSPIoTLin) {
                updates.idTSPIoTLin = defaultTSPIoTSettings.idTSPIoTLin
            }
            if (!props.cashRegisterDto.versionTSPIoT) {
                updates.versionTSPIoT = defaultTSPIoTSettings.versionTSPIoT.value
            }
            if (!props.cashRegisterDto.offlineLogin) {
                updates.offlineLogin = defaultTSPIoTSettings.offlineLogin;
            }
            if (!props.cashRegisterDto.offlinePassword) {
                updates.offlinePassword = defaultTSPIoTSettings.offlinePassword;
            }
            if (!props.cashRegisterDto.offlineServer) {
                updates.offlineServer = defaultTSPIoTSettings.offlineServer;
            }
            if (!props.cashRegisterDto.offlinePort) {
                updates.offlinePort = defaultTSPIoTSettings.offlinePort;
            }
            if (!props.cashRegisterDto.offlineVersion) {
                updates.offlineVersion = defaultTSPIoTSettings.offlineVersion.value;
            }
            
            if (Object.keys(updates).length > 0) {
                props.setRegisterDto({
                    ...props.cashRegisterDto,
                    ...updates
                });
            }
        }
    }, [props.cashRegisterDto.useTSPIoT, props.cashRegisterDto.versionTSPIoT]);

    return (
        <div className={styles.wrapper}>
            <Accordion insertedAccordion className={styles.accardion} opened={true} caption='Настройка' id="additionalInvoiceInfo">
                <DefaultGrid
                    gridId={uuidv4()}
                    data={additionalSettings}
                    filter={gridFilter}
                    hiddenPagination={undefined}
                    totalCount={totalCount}
                    plugin={SettingsAdditionalPluginSettings}
                    selectedItem={selectedItem}
                    onSelect={(row) => {
                        setSelectedItem(row)
                    }}
                    onSort={(i) => { dispatchGridFilter({ type: "sort", payload: i.propertyName }) }}
                    onFilterDelete={(i) => { dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName }) }}
                    onPageNumberChange={(n) => { dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } }) }}
                    onNumberPerPageChange={(n) => {
                        dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })
                    }}
                />
            </Accordion>
            <Accordion insertedAccordion className={styles.accardion} opened={true} caption='Описание' id="additionalInvoiceInfo">
                <div className={styles.contentWrapper}>
                    <textarea value={descriptionText} rows={8} className={styles.descriptionTextArea} />
                    {
                        selectAdditionalSetting !== undefined && selectAdditionalSetting.data === undefined && selectAdditionalSetting.serialNumber !== 9 &&
                        <Checkbox
                            checked={selectAdditionalSetting.value}
                            name={selectAdditionalSetting?.setting}
                            label={descriptionText}
                            onChange={(value) => {
                                setSelectAdditionalSetting({ ...selectAdditionalSetting, value: value })
                                props.setRegisterDto({
                                    ...props.cashRegisterDto,
                                    useTSPIoT: selectAdditionalSetting.serialNumber == 1 ? value : props.cashRegisterDto.useTSPIoT,
                                    enableMixPayment: selectAdditionalSetting.serialNumber == 2 ? value : props.cashRegisterDto.enableMixPayment,
                                    enableAcquiring: selectAdditionalSetting.serialNumber == 3 ? value : props.cashRegisterDto.enableAcquiring,
                                    enableProvisorInterface: selectAdditionalSetting.serialNumber == 4 ? value : props.cashRegisterDto.enableProvisorInterface,
                                    askBeforeChequePrint: selectAdditionalSetting.serialNumber == 5 ? value : props.cashRegisterDto.askBeforeChequePrint,
                                    visualProcessingRV: selectAdditionalSetting.serialNumber == 6 ? value : props.cashRegisterDto.visualProcessingRV,
                                    printSectionReport: selectAdditionalSetting.serialNumber == 8 ? value : props.cashRegisterDto.printSectionReport,
                                    isAutoUpdate: selectAdditionalSetting.serialNumber == 10 ? value : props.cashRegisterDto.isAutoUpdate,
                                })
                            }} />
                    }
                    {
                        selectAdditionalSetting !== undefined && selectAdditionalSetting.serialNumber === 9 &&
                        <>
                            <span>{t("modals.additionalTab.valueTextCheque")}</span>
                            <TextAreaInput
                                value={initialValueEndText}
                                largeTextArea
                                onChange={handleEndTextChequeChange}
                            >
                                {initialValueEndText}
                            </TextAreaInput>
                        </>
                    }
                    {
                        selectAdditionalSetting?.data !== undefined && selectAdditionalSetting.data &&

                        <FlexColumn>
                            <Checkbox
                                checked={selectAdditionalSetting.data.printPriceForItemWithoutDiscount}
                                name={t("modals.additionalTab.printPriceForItemWithoutDiscount")}
                                label={t("modals.additionalTab.printPriceForItemWithoutDiscount")}
                                onChange={(value) => {
                                    if (selectAdditionalSetting?.data)
                                        setSelectAdditionalSetting({ ...selectAdditionalSetting, data: { ...selectAdditionalSetting.data, printPriceForItemWithoutDiscount: value } })

                                    props.setRegisterDto({
                                        ...props.cashRegisterDto,
                                        printDiscountSettings: { ...props.cashRegisterDto.printDiscountSettings, printPriceForItemWithoutDiscount: value }
                                    })
                                }}
                            />

                            <Checkbox
                                checked={selectAdditionalSetting.data.printSummForItemWithoutDiscount}
                                name={t("modals.additionalTab.printSummForItemWithoutDiscount")}
                                label={t("modals.additionalTab.printSummForItemWithoutDiscount")}
                                onChange={(value) => {
                                    if (selectAdditionalSetting?.data)
                                        setSelectAdditionalSetting({ ...selectAdditionalSetting, data: { ...selectAdditionalSetting.data, printSummForItemWithoutDiscount: value } })

                                    props.setRegisterDto({
                                        ...props.cashRegisterDto,
                                        printDiscountSettings: { ...props.cashRegisterDto.printDiscountSettings, printSummForItemWithoutDiscount: value }
                                    })
                                }}
                            />

                            <Checkbox
                                checked={selectAdditionalSetting.data.printSummDiscount}
                                name={t("modals.additionalTab.printSummDiscount")}
                                label={t("modals.additionalTab.printSummDiscount")}
                                onChange={(value) => {
                                    if (selectAdditionalSetting?.data)
                                        setSelectAdditionalSetting({ ...selectAdditionalSetting, data: { ...selectAdditionalSetting.data, printSummDiscount: value } })

                                    props.setRegisterDto({
                                        ...props.cashRegisterDto,
                                        printDiscountSettings: { ...props.cashRegisterDto.printDiscountSettings, printSummDiscount: value }
                                    })
                                }}
                            />

                            <Checkbox
                                checked={selectAdditionalSetting.data.printSummForChequeWithoutDiscount}
                                name={t("modals.additionalTab.printSummForChequeWithoutDiscount")}
                                label={t("modals.additionalTab.printSummForChequeWithoutDiscount")}
                                onChange={(value) => {
                                    if (selectAdditionalSetting?.data)
                                        setSelectAdditionalSetting({ ...selectAdditionalSetting, data: { ...selectAdditionalSetting.data, printSummForChequeWithoutDiscount: value } })

                                    props.setRegisterDto({
                                        ...props.cashRegisterDto,
                                        printDiscountSettings: { ...props.cashRegisterDto.printDiscountSettings, printSummForChequeWithoutDiscount: value }
                                    })
                                }}
                            />

                            <Checkbox
                                checked={selectAdditionalSetting.data.printDiscountForItem}
                                name={t("modals.additionalTab.printDiscountForItem")}
                                label={t("modals.additionalTab.printDiscountForItem")}
                                onChange={(value) => {
                                    if (selectAdditionalSetting?.data)
                                        setSelectAdditionalSetting({ ...selectAdditionalSetting, data: { ...selectAdditionalSetting.data, printDiscountForItem: value } })

                                    props.setRegisterDto({
                                        ...props.cashRegisterDto,
                                        printDiscountSettings: { ...props.cashRegisterDto.printDiscountSettings, printDiscountForItem: value }
                                    })
                                }}
                            />

                            <Select
                                className={styles.select}
                                onSelect={(option) => {
                                    props.setRegisterDto({
                                        ...props.cashRegisterDto,
                                        printDiscountSettings: { ...props.cashRegisterDto.printDiscountSettings, printTypeOfDiscount: option.value }
                                    })
                                }}
                                value={{
                                    displayName: props.cashRegisterDto?.printDiscountSettings?.printTypeOfDiscount ?? options[0].displayName,
                                    value: props.cashRegisterDto?.printDiscountSettings?.printTypeOfDiscount ?? options[0].value
                                }}
                                options={options}

                            />
                        </FlexColumn>
                    }
                    {
                        props.cashRegisterDto?.useTSPIoT === true && selectAdditionalSetting?.serialNumber === 1 && (
                            <>
                                <div className={styles.TSPIoTWrapper}>
                                    <div className={styles.TSPIoTTitleWrapper}>
                                        {t("modals.additionalTab.settingsTSPIoT")}
                                    </div>
                                    <TextInput
                                        required
                                        error={urlTSPIoTError}
                                        label={t("modals.additionalTab.addressTSPIoT")}
                                        inline
                                        value={props.cashRegisterDto.urlTSPIoT}
                                        onChange={handleUrlTSPIoTChange}
                                    />
                                    <GridWrapper cols={2}>
                                        <div className={styles.inputWrapper}>
                                            <NumberInput
                                                label={t("modals.additionalTab.timeOutTSPIoT")}
                                                style={{ width: 40 }}
                                                inline
                                                min={0}
                                                value={props.cashRegisterDto.timeOut}
                                                onChange={(value) => {
                                                    props.setRegisterDto({
                                                        ...props.cashRegisterDto,
                                                        timeOut: value
                                                    })
                                                }}
                                            />
                                            <span className={styles.labelSuf}>{t("modals.additionalTab.timeOutSuf")}</span>
                                        </div>
                                        <Select
                                            label={t("modals.additionalTab.versionTSPIoT")}
                                            style={{width: '100%'}}
                                            inline
                                            className={styles.select}
                                            onSelect={(versionTSPIoTOptions) => {
                                                props.setRegisterDto({
                                                    ...props.cashRegisterDto,
                                                    versionTSPIoT: versionTSPIoTOptions.value
                                                })
                                            }}
                                            value={{
                                                displayName: versionTSPIoTOptions.find(v => v.value === props.cashRegisterDto?.versionTSPIoT)?.displayName ?? versionTSPIoTOptions[versionTSPIoTOptions.length - 1].displayName,
                                                value: props.cashRegisterDto?.versionTSPIoT ?? versionTSPIoTOptions[versionTSPIoTOptions.length - 1].value
                                            }}
                                            options={versionTSPIoTOptions}
                                        />
                                    </GridWrapper>
                                </div>
                                <div className={styles.TSPIoTWrapper}>
                                    <div className={styles.TSPIoTTitleWrapper}>
                                        {t("modals.additionalTab.validDate")}
                                    </div>
                                    <Checkbox
                                        checked={props.cashRegisterDto?.isControlValidDate}
                                        name={t("modals.additionalTab.controlValidDate")}
                                        label={t("modals.additionalTab.controlValidDate")}
                                        onChange={(value) => {
                                            props.setRegisterDto({
                                                ...props.cashRegisterDto,
                                                isControlValidDate: value
                                            })
                                        }}
                                    />
                                    <GridWrapper cols={1}>
                                        <div className={styles.inputWrapper}>
                                            <NumberInput
                                                label={t("modals.additionalTab.daysStopSale")}
                                                style={{ width: 40 }}
                                                inline
                                                min={0}
                                                value={props.cashRegisterDto.daysStopSaleUnvalidDate}
                                                onChange={(value) => {
                                                    props.setRegisterDto({
                                                        ...props.cashRegisterDto,
                                                        daysStopSaleUnvalidDate: value
                                                    })
                                                }}
                                            />
                                            <span className={styles.labelSuf}>{t("modals.additionalTab.daysStopSaleSuf")}</span>
                                        </div>
                                    </GridWrapper>
                                </div>
                                <div className={styles.TSPIoTWrapper}>
                                    <Checkbox
                                        checked={props.cashRegisterDto?.allowGoodsOGV}
                                        name={t("modals.additionalTab.allowGoodsOGV")}
                                        label={t("modals.additionalTab.allowGoodsOGV")}
                                        onChange={(value) => {
                                            props.setRegisterDto({
                                                ...props.cashRegisterDto,
                                                allowGoodsOGV: value
                                            })
                                        }}
                                    />
                                    {props.cashRegisterDto.allowGoodsOGV && (
                                        <TextInput
                                            required
                                            error={messageOGVError}
                                            label={t("modals.additionalTab.allowGoodsOGVTextTitle")}
                                            inline
                                            value={props.cashRegisterDto.messageOGV || ''}
                                            onChange={handleMessageOGVChange}
                                        />
                                    )}
                                </div>
                                <div className={styles.TSPIoTWrapper}>
                                    <TextInput
                                        disabled={!isAdministrator}
                                        label={t("modals.additionalTab.idTSPIoTWin")}
                                        inline
                                        value={props.cashRegisterDto.idTSPIoTWin}
                                        onChange={(value: string) => {
                                            props.setRegisterDto({
                                                ...props.cashRegisterDto,
                                                idTSPIoTWin: value
                                            })
                                        }}
                                    />
                                    <TextInput
                                        disabled={!isAdministrator}
                                        label={t("modals.additionalTab.idTSPIoTLin")}
                                        inline
                                        value={props.cashRegisterDto.idTSPIoTLin}
                                        onChange={(value: string) => {
                                            props.setRegisterDto({
                                                ...props.cashRegisterDto,
                                                idTSPIoTLin: value
                                            })
                                        }}
                                    />
                                </div>
                                <Accordion
                                    id={`authorizationSettings ${props.cashRegisterDto.idGlobal}`}
                                    opened
                                    caption={t("modals.additionalTab.settings")}
                                    bottom={false}
                                >

                                <div className={styles.TSPIoTWrapper}>
                                    <div className={styles.TSPIoTTitleWrapper}>
                                        {t("modals.additionalTab.authorizationSettings")}
                                    </div>
                                    <FlexRow>
                                        <GridWrapper cols={3}>
                                            <TextInput
                                                disabled={!isAdministrator || props.cashRegisterDto?.versionTSPIoT === "VER_1"}
                                                label={t("modals.additionalTab.offlineLogin")}
                                                inline
                                                value={props.cashRegisterDto.offlineLogin}
                                                onChange={(value: string) => {
                                                    props.setRegisterDto({
                                                        ...props.cashRegisterDto,
                                                        offlineLogin: value
                                                    })
                                                }}
                                            />
                                            <TextInput
                                                disabled={!isAdministrator || props.cashRegisterDto?.versionTSPIoT === "VER_1"}
                                                label={t("modals.additionalTab.offlinePassword")}
                                                inline
                                                value={props.cashRegisterDto.offlinePassword}
                                                onChange={(value: string) => {
                                                    props.setRegisterDto({
                                                        ...props.cashRegisterDto,
                                                        offlinePassword: value
                                                    })
                                                }}
                                            />

                                            <Select
                                                className={styles.select}
                                                label={t("modals.additionalTab.offlineVersion")}
                                                inline
                                                onSelect={(versions) => {
                                                    props.setRegisterDto({
                                                        ...props.cashRegisterDto,
                                                        offlineVersion: versions.value
                                                    })
                                                }}
                                                value={{
                                                    displayName: offlineVersionsOptions.find(v => v.value === props.cashRegisterDto?.offlineVersion)?.displayName ?? offlineVersionsOptions[offlineVersionsOptions.length - 1].displayName,
                                                    value: props.cashRegisterDto?.offlineVersion ?? offlineVersionsOptions[offlineVersionsOptions.length - 1].value
                                                }}
                                                options={offlineVersionsOptions}
                                                disabled={!isAdministrator || props.cashRegisterDto?.versionTSPIoT === "VER_1"}
                                            />
                                        </GridWrapper>
                                    </FlexRow>
                                    <FlexRow>
                                        <GridWrapper cols={3}>
                                            <TextInput
                                                disabled={!isAdministrator || props.cashRegisterDto?.versionTSPIoT === "VER_1"}
                                                label={t("modals.additionalTab.offlineServer")}
                                                inline
                                                value={props.cashRegisterDto.offlineServer}
                                                onChange={(value: string) => {
                                                    props.setRegisterDto({
                                                        ...props.cashRegisterDto,
                                                        offlineServer: value
                                                    })
                                                }}
                                            />
                                            <TextInput
                                                disabled={!isAdministrator || props.cashRegisterDto?.versionTSPIoT === "VER_1"}
                                                label={t("modals.additionalTab.offlinePort")}
                                                inline
                                                value={props.cashRegisterDto.offlinePort}
                                                onChange={(value: string) => {
                                                    props.setRegisterDto({
                                                        ...props.cashRegisterDto,
                                                        offlinePort: value
                                                    })
                                                }}
                                            />
                                            <TextInput
                                                disabled={!isAdministrator || props.cashRegisterDto?.versionTSPIoT === "VER_1"}
                                                label={t("modals.additionalTab.offlineToken")}
                                                inline
                                                value={props.cashRegisterDto.offlineToken}
                                                onChange={(value: string) => {
                                                    props.setRegisterDto({
                                                        ...props.cashRegisterDto,
                                                        offlineToken: value
                                                    })
                                                }}
                                            />
                                        </GridWrapper>
                                    </FlexRow>
                                </div>
                                <div className={`${styles.TSPIoTWrapper} ${styles.contentWrapper}`}>
                                    <Checkbox
                                        disabled={!isAdministrator || props.cashRegisterDto?.versionTSPIoT === "VER_1"}
                                        checked={props.cashRegisterDto?.offlineRegimeEnabled}
                                        name={t("modals.additionalTab.offlineRegimeEnabled")}
                                        label={t("modals.additionalTab.offlineRegimeEnabled")}
                                        onChange={(value) => {
                                            props.setRegisterDto({
                                                ...props.cashRegisterDto,
                                                offlineRegimeEnabled: value
                                            })
                                        }}
                                    />
                                    <Checkbox
                                        disabled={!isAdministrator || props.cashRegisterDto?.versionTSPIoT === "VER_1"}
                                        checked={props.cashRegisterDto?.allowChequePrintLMCZNoAnswer}
                                        name={t("modals.additionalTab.allowChequePrintLMCZNoAnswer")}
                                        label={t("modals.additionalTab.allowChequePrintLMCZNoAnswer")}
                                        onChange={(value) => {
                                            props.setRegisterDto({
                                                ...props.cashRegisterDto,
                                                allowChequePrintLMCZNoAnswer: value
                                            })
                                        }}
                                    />
                                    <p>{t("modals.additionalTab.warningMessage_1")}</p>
                                    <p>{t("modals.additionalTab.warningMessage_2")}</p>

                                </div>
                                </Accordion>

                            </>
                        )
                    }
                </div>
            </Accordion>
        </div>
    )
}